import React, {useEffect, useState} from 'react';
import {Box, Stack} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {useNavigate, useParams} from 'react-router-dom';
import {useEffectFn} from '@ngneat/effects-hooks';
import {useObservable} from '@ngneat/react-rxjs';
import {finalize, Observable} from 'rxjs';
import {useSnackbar} from 'notistack';
import {emitOnce} from '@ngneat/elf';
import {GridValueGetterParams} from '@mui/x-data-grid-pro';

import {
  searchSuppliersUnitsEffect,
  SuppliersUnit,
  suppliersUnitsQuery,
  suppliersUnitsService
} from '@store/suppliers/units';
import {GlobalStatus} from '@store/common/globalStatus.enum';

import SearchComponent from '@components/inputs/search/Search.component';
import TableFiltersComponent from '@components/table/filters/TableFilters.component';
import NewTableComponent from '@components/table/NewTable.component';
import ConfirmModal from '@components/modal/Confirm.modal';

import {ConfirmActionProps, OtherActionType} from '@utils/table/columnTypes/actions.columnType';
import COLUMN_TYPES from '@utils/table/columnTypes.table';

const AllSupplierUnitsTab = () => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {supplierId} = useParams();
  const {enqueueSnackbar} = useSnackbar();

  const searchSuppliersUnits = useEffectFn(searchSuppliersUnitsEffect);

  const [openConfirmAction, setOpenConfirmAction] = useState<ConfirmActionProps | false>(false);

  const [{suppliersUnits, loading}] = useObservable(suppliersUnitsQuery.suppliersUnits$);
  const [selectedSuppliersUnits] = useObservable(suppliersUnitsQuery.selectedSuppliersUnits$);

  const [filterStatus] = useObservable(suppliersUnitsQuery.status$);
  const [filters] = useObservable(suppliersUnitsQuery.filters$);
  const [sortModel] = useObservable(suppliersUnitsQuery.sort$);
  const [tablePagination] = useObservable(suppliersUnitsQuery.pagination$);

  useEffect(() => {
    if (supplierId) suppliersUnitsService.resetFilters(supplierId);
  }, [supplierId]);

  useEffect(() => {
    searchSuppliersUnits({status: filterStatus, filters, tablePagination, sort: sortModel});
  }, [filters, filterStatus, searchSuppliersUnits, sortModel, tablePagination]);

  const handleConfirmValidation = (type: OtherActionType, supplierUnit: SuppliersUnit) => (handleStopLoading: () => void) => {
    let validationObservable: Observable<any> | undefined = undefined;
    switch (type) {
      case OtherActionType.DELETE:
        validationObservable = suppliersUnitsService.deleteSuppliersUnitInList(supplierUnit.id);
        break;
      case OtherActionType.ARCHIVE:
        validationObservable = suppliersUnitsService.updateSuppliersUnitsStatusInList(supplierUnit.id, GlobalStatus.ARCHIVED);
        break;
    }
    validationObservable?.pipe(finalize(handleStopLoading)).subscribe({
      next: () => {
        enqueueSnackbar(t(`${OtherActionType.translationKey(type)}.success.supplierUnit`), {variant: 'success'});
        setOpenConfirmAction(false);
      },
      error: (err) => enqueueSnackbar(err.text, err.options),
    });
  };

  return (
    <Stack flex={1} spacing={2} pb="25px">
      <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between">
        <Box width="500px">
          <SearchComponent
            value={filters.search}
            handleChange={(value) => {
              emitOnce(() => {
                suppliersUnitsService.setFilters('search')(value);
                suppliersUnitsService.setPagination('page')(1);
              });
            }}/>
        </Box>
        <TableFiltersComponent
          filters={filters}
          handleSave={suppliersUnitsService.setFilters('tableFilters')}
          handleReset={() => supplierId ? suppliersUnitsService.resetFilters(supplierId) : undefined}/>
      </Stack>
      <NewTableComponent
        loading={loading}
        columns={[
          COLUMN_TYPES.TEXT_BOLD({
            field: 'organisationUnit.id',
            label: t('clients.clientUnitsTable.id'),
            sortable: true,
            valueGetter: (params: GridValueGetterParams<any, SuppliersUnit>) => params.row.id,
            width: 100
          }),
          COLUMN_TYPES.TEXT_BOLD({
            field: 'organisationUnit.name',
            label: t('clients.clientUnitsTable.name'),
            sortable: true,
            valueGetter: (params: GridValueGetterParams<any, SuppliersUnit>) => params.row.name,
            fullWidth: true,
            minWidth: 350,
          }),
          COLUMN_TYPES.TEXT({field: 'phone', label: t('clients.clientUnitsTable.phone'), width: 130}),
          COLUMN_TYPES.TEXT({
            field: 'email',
            color: 'secondary',
            label: t('clients.clientUnitsTable.email'),
            fullWidth: true,
            minWidth: 300
          }),
          COLUMN_TYPES.TEXT({field: 'city', color: 'secondary', label: t('clients.clientUnitsTable.city'), width: 250}),
          COLUMN_TYPES.TEXT({
            field: 'organisationUnit.region',
            color: 'secondary',
            sortable: true,
            valueGetter: (params: GridValueGetterParams<any, SuppliersUnit>) => params.row.region,
            label: t('suppliers.supplierUnitsTable.region'),
            width: 150
          }),
          COLUMN_TYPES.TEXT({
            field: 'country',
            color: 'secondary',
            label: t('clients.clientUnitsTable.country'),
            width: 150
          }),
          COLUMN_TYPES.ACTIONS({
            handleShowRow: (supplierUnit: SuppliersUnit) => navigate(`${supplierUnit.id}`),
            otherActions: [
              {
                type: OtherActionType.ARCHIVE,
                handleClick: (supplierUnit: SuppliersUnit) => setOpenConfirmAction({
                  type: OtherActionType.ARCHIVE,
                  row: supplierUnit
                }),
                hidden: (supplierUnit: SuppliersUnit) => supplierUnit.status === GlobalStatus.ARCHIVED || filterStatus === GlobalStatus.ARCHIVED,
              },
              {
                type: OtherActionType.DELETE,
                handleClick: (supplierUnit: SuppliersUnit) => setOpenConfirmAction({
                  type: OtherActionType.DELETE,
                  row: supplierUnit
                }),
              },
            ]
          }),
        ]}
        handlePageSizeChange={suppliersUnitsService.setPagination('limit')}
        pageSize={tablePagination.limit}
        handlePageChange={(page) => suppliersUnitsService.setPagination('page')(page + 1)}
        page={tablePagination.page - 1}
        handleSortModelChange={suppliersUnitsService.setSort}
        sortModel={[sortModel]}
        handleRowSelected={suppliersUnitsService.setSelectedSuppliersUnits}
        rowsSelected={selectedSuppliersUnits}
        handleReload={() => searchSuppliersUnits({status: filterStatus, filters, tablePagination, sort: sortModel})}
        totalRows={suppliersUnitsQuery.totalRows}
        data={suppliersUnits}/>
      {!!openConfirmAction && (
        <ConfirmModal
          handleClose={() => setOpenConfirmAction(false)}
          description={t(`${OtherActionType.translationKey(openConfirmAction.type)}.description.supplierUnit`)}
          handleValidate={handleConfirmValidation(openConfirmAction.type, openConfirmAction.row)}
          title={t(`${OtherActionType.translationKey(openConfirmAction.type)}.title`)}
          validateButton={OtherActionType.label(openConfirmAction.type)}/>
      )}
    </Stack>
  );
};

export default AllSupplierUnitsTab;
